import React, { useState, useRef, useEffect } from 'react';
import { FiEye, FiEyeOff } from 'react-icons/fi';

interface InputFieldProps {
  icon?: React.ReactNode;
  label: string;
  type?: 'text' | 'select' | 'number' | 'email' | 'password' | 'date' | 'textarea';
  value: string;
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => void;
  onBlur?: () => void; // Add onBlur support
  onFocus?: () => void; // Add onFocus support
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  labelClassName?: string;
  min?: string; // Add min property for number inputs
  max?: string; // Add max property for number inputs
  options?: Array<string | {
    value: string;
    label: string;
    className?: string;
    disabled?: boolean; // New prop to disable specific options
  }>;
  formatOptions?: boolean; // New prop to determine if options should be formatted
  onClick?: () => void; // Added onClick handler for input field
  onWheel?: (event: React.WheelEvent<HTMLInputElement>) => void; // Add wheel event handler
  error?: string; // Add error support
  helpText?: string; // Add helpText support
  required?: boolean;
}

// Format status helper function
const formatStatusText = (status: any): string => {
  if (!status) return '';

  // Handle non-string values
  const statusStr =
    typeof status === 'string' ? status : status.value || status.label || String(status);

  // Replace underscores with spaces
  let formattedStatus = statusStr.replace(/_/g, ' ');

  // Convert to title case (capitalize first letter of each word)
  formattedStatus = formattedStatus
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

  // Handle special cases
  switch (statusStr.toUpperCase()) {
    case 'IN_PROGRESS':
      return 'In Progress';
    case 'PAYMENT_RECEIVED':
      return 'Payment Received';
    default:
      return formattedStatus;
  }
};

const InputField: React.FC<InputFieldProps> = ({
  icon,
  label,
  type = 'text',
  value,
  onChange,
  onBlur, // Add onBlur
  onFocus, // Add onFocus
  disabled = false,
  placeholder = '',
  className = '',
  labelClassName = '',
  options = [],
  formatOptions = true, // Default to formatting options
  onClick,
  onWheel,
  min,
  max,
  error, // Add error
  helpText, // Add helpText
  required,
}) => {
  // Set default min and max for date inputs if not provided
  const dateMin = type === 'date' && !min ? '1900-01-01' : min;
  const dateMax = type === 'date' && !max ? '2100-12-31' : max;
  // State for custom select dropdown
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOptionLabel, setSelectedOptionLabel] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // State for password visibility
  const [showPassword, setShowPassword] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Initialize selected option label
  useEffect(() => {
    if (type === 'select' && value) {
      const selectedOption = options.find(opt =>
        typeof opt === 'string' ? opt === value : opt.value === value
      );

      if (selectedOption) {
        const label = typeof selectedOption === 'string' ? selectedOption : selectedOption.label;

        setSelectedOptionLabel(formatOptions ? formatStatusText(label) : label);
      } else {
        setSelectedOptionLabel('');
      }
    }
  }, [value, options, formatOptions]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
    if (!isOpen) {
      setSearchTerm('');
    }
  }, [isOpen]);

  // Handle custom select option click
  const handleOptionClick = (optionValue: string, optionLabel: string) => {
    if (onChange) {
      // Create a synthetic event object
      const syntheticEvent = {
        target: { value: optionValue },
      } as React.ChangeEvent<HTMLSelectElement>;

      onChange(syntheticEvent);
    }
    setSelectedOptionLabel(formatOptions ? formatStatusText(optionLabel) : optionLabel);
    setIsOpen(false);
  };

  return (
    <div className="flex flex-col w-full bg-white relative">
      <label
        className={`absolute left-5 bg-white px-2 z-10 
        flex items-center gap-1 text-gray-500 text-sm font-medium ${labelClassName}`}
      >
        {icon && <span className="text-gray-400 stroke-[0.5] stroke-current">{icon}</span>}
        {label}
      </label>
      {type === 'textarea' ? (
        <textarea
          value={value}
          onChange={onChange as React.ChangeEventHandler<HTMLTextAreaElement>}
          onBlur={onBlur}
          disabled={disabled}
          placeholder={placeholder}
          className={`mt-2 border-2 pl-6 rounded-xl px-4 py-4 text-gray-800 font-semibold 
          focus:outline-none focus:ring-2 focus:ring-blue-500 
          border-gray-200 hover:border-gray-300 focus:border-blue-500
          transition-all duration-200
          ${disabled
              ? 'bg-gray-100 text-gray-500 cursor-not-allowed border-gray-200'
              : 'bg-white hover:border-gray-300'
            } ${className}`}
          rows={4}
        />
      ) : type === 'select' ? (
        <div className="relative mt-2" ref={dropdownRef}>
          {/* Custom Select Dropdown Button */}
          <div
            onClick={() => {
              if (!disabled) {
                setIsOpen(!isOpen);
                // Call onFocus when opening the dropdown
                if (!isOpen && onFocus) {
                  onFocus();
                }
              }
            }}
            className={`border-2 rounded-xl px-4 py-4 text-gray-800 font-semibold 
            flex justify-between items-center 
            border-gray-200 hover:border-gray-300 focus:border-blue-500
            transition-all duration-200
            ${disabled
                ? 'bg-gray-100 text-gray-500 cursor-not-allowed border-gray-200'
                : 'bg-white cursor-pointer hover:border-gray-300'
              } ${className}`}
          >
            <span>{selectedOptionLabel || placeholder || `Select ${label}`}</span>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className={`h-5 w-5 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </div>

          {/* Custom Dropdown Options */}
          {isOpen && (
            <div
              className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-hidden left-0 right-0"
            >
              {/* Search Input */}
              <div className="sticky top-0 bg-white border-b border-gray-200 p-2">
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>

              {/* Options List */}
              <div className="overflow-y-auto max-h-48">
                {options.length > 0 ? (
                  (() => {
                    const filteredOptions = options.filter((option) => {
                      const optionLabel = typeof option === 'string' ? option : option.label;
                      return optionLabel.toLowerCase().includes(searchTerm.toLowerCase());
                    });

                    return filteredOptions.length > 0 ? (
                      filteredOptions.map((option, index) => {
                        const optionValue = typeof option === 'string' ? option : option.value;
                        const optionLabel = typeof option === 'string' ? option : option.label;
                        const optionClass = typeof option === 'string' ? '' : option.className || '';
                        const optionDisabled = typeof option === 'string' ? false : option.disabled === true;

                        const isSelected = optionValue === value;

                        return (
                          <div
                            key={index}
                            className={`px-4 py-2 text-ellipsis overflow-hidden ${optionDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-gray-50'} ${optionClass} ${isSelected ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                              }`}
                            onClick={() => !optionDisabled && handleOptionClick(optionValue, optionLabel)}
                          >
                            {formatOptions ? (
                              <div className="flex justify-between items-center w-full">
                                <span className="truncate max-w-[70%]">
                                  {formatStatusText(optionLabel.replace(' (Under revision)', ''))}
                                  {optionDisabled && <span className="text-gray-400 ml-1">(Invalid transition)</span>}
                                </span>
                                {optionLabel.includes('(Under revision)') && (
                                  <span className="text-gray-500 ml-auto whitespace-nowrap">(Under revision)</span>
                                )}
                              </div>
                            ) : (
                              <span className="truncate block">
                                {optionLabel}
                                {optionDisabled && <span className="text-gray-400 ml-1">(Invalid transition)</span>}
                              </span>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <div className="px-4 py-2 text-gray-500">No matches found</div>
                    );
                  })()
                ) : (
                  <div className="px-4 py-2 text-gray-500">No options available</div>
                )}
              </div>
            </div>
          )}

          {/* Hidden select element for form submission */}
          <select
            value={value}
            onChange={onChange}
            disabled={disabled}
            className="opacity-0 absolute w-0 h-0"
          >
            <option value="">Select {label}</option>
            {options.map((option, index) => {
              const optionValue = typeof option === 'string' ? option : option.value;
              return <option key={index} value={optionValue}></option>;
            })}
          </select>
        </div>
      ) : (
        <div className="relative mt-2">
          <input
            type={type === 'password' && showPassword ? 'text' : type}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            onWheel={onWheel}
            disabled={disabled}
            placeholder={placeholder}
            onClick={onClick}
            min={dateMin}
            max={dateMax}
            className={`w-full border-2 pl-6 rounded-xl px-4 py-4 text-gray-800 font-semibold 
            focus:outline-none focus:ring-2 focus:ring-blue-500 
            border-gray-200 hover:border-gray-300 focus:border-blue-500
            transition-all duration-200
            ${disabled
                ? 'bg-gray-100 text-gray-500 cursor-not-allowed border-gray-200'
                : 'bg-white hover:border-gray-300'
              } ${type === 'password' ? 'pr-12' : ''} ${className}`}
          />
          {type === 'password' && (
            <button
              type="button"
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <FiEyeOff size={20} /> : <FiEye size={20} />}
            </button>
          )}
        </div>
      )}

      {/* Error message */}
      {error && (
        <p className="text-red-500 text-xs mt-1">{error}</p>
      )}

      {/* Help text */}
      {helpText && !error && (
        <p className="text-gray-500 text-xs mt-1">{helpText}</p>
      )}
    </div>
  );
};

export default InputField;
